import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alan,
  BosDurum,
  Buton,
  FiltreKarti,
  Iskelet,
  Kart,
  MetinAlani,
  Rozet,
  Secim,
  Tablo,
} from "@koclukpanel/ui";
import {
  ApiHatasi,
  agacGetir,
  alanlariGetir,
  kaynakHavuzu,
  kitapArsivle,
  kitapEkle,
  kitapGuncelle,
  kitapTopluEkle,
  type KitapGirdisi,
  type KitapSatiri,
  type TopluKaynakSonuc,
} from "./api";
import { SayfaBasligiEki } from "./Kabuk";

/**
 * KAYNAK KİTAP HAVUZU — F-RES-01
 *
 * ================================================================
 * SUNUCU 29.08.2026'DA YAZILDI, EKRANI HİÇ OLMADI
 * ================================================================
 * On uç yazıldı ve canlı doğrulandı; hiçbiri bir arayüzden
 * çağrılmıyordu. Yani kaynak kitap havuzu sistemde VARDI ve
 * kullanılamıyordu — program hazırlarken seçilecek liste hiç
 * doldurulamıyordu.
 *
 * ================================================================
 * ARŞİVLEME SİLME DEĞİL — VE SAYIYLA UYARIYOR
 * ================================================================
 * Bir kitap havuzdan çıkarıldığında öğrencilerin listesindeki
 * kayıtlar OLDUĞU GİBİ kalıyor; yarım kalan ilerleme kaybolmuyor.
 * Ekran arşivlemeden önce "bu kitabı kaç öğrenci kullanıyor" sayısını
 * gösteriyor: sayı olmadan karar, körlemesine verilirdi.
 */
export function KaynakHavuzu({ yonetebilir = false }: { yonetebilir?: boolean }) {
  const [alanId, setAlanId] = useState("");
  const [dersId, setDersId] = useState("");
  const [ara, setAra] = useState("");
  const [arsivDahil, setArsivDahil] = useState(false);
  const [acik, setAcik] = useState<"kapali" | "tek" | "toplu">("kapali");

  const alanlar = useQuery({ queryKey: ["alanlar"], queryFn: alanlariGetir, retry: false });
  const agac = useQuery({
    queryKey: ["agac", alanId],
    queryFn: () => agacGetir(alanId),
    enabled: alanId !== "",
    retry: false,
  });

  const havuz = useQuery({
    queryKey: ["kaynak-havuz", dersId, ara, arsivDahil],
    queryFn: () =>
      kaynakHavuzu({
        ders: dersId || undefined,
        ara: ara.trim() || undefined,
        arsiv: arsivDahil,
      }),
    retry: false,
  });

  const dersler = (agac.data ?? []).filter((d) => d.tur === "ders");

  return (
    <div className="flex flex-col gap-4">
      <SayfaBasligiEki aciklama="Ders, konu ve kaynakları alan bazında yönetin; listeleri filtreleyip çağırın." />
      {/* UNİKOÇ "Kayıtlar" kartı: Alan / Ders / Ara filtreleri tek beyaz kartta (tarama-1 §3.14). */}
      <FiltreKarti baslik="Kaynak Kayıtları">
        <div className="flex flex-wrap items-end gap-2">
          <Secim
            etiket="Alan"
            value={alanId}
            onChange={(e) => {
              setAlanId(e.target.value);
              setDersId("");
            }}
            className="w-48"
          >
            <option value="">Bütün alanlar</option>
            {(alanlar.data ?? []).map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </Secim>

          <Secim
            etiket="Ders"
            value={dersId}
            onChange={(e) => setDersId(e.target.value)}
            disabled={alanId === ""}
            className="w-48"
          >
            <option value="">{alanId === "" ? "Önce alan seçin" : "Bütün dersler"}</option>
            {dersler.map((d) => (
              <option key={d.id} value={d.id}>
                {d.ad}
              </option>
            ))}
          </Secim>

          <div className="w-56">
            <Alan
              etiket="Ara"
              value={ara}
              onChange={(e) => setAra(e.target.value)}
              placeholder="Kitap ya da yayınevi"
            />
          </div>

          <label className="min-h-dokunma text-notr-800 flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="accent-marka-600 size-4"
              checked={arsivDahil}
              onChange={(e) => setArsivDahil(e.target.checked)}
            />
            Arşivlenenleri de göster
          </label>

          {yonetebilir && (
            <div className="ml-auto flex flex-wrap gap-2">
              <Buton
                gorunum="ikincil"
                onClick={() => setAcik((a) => (a === "toplu" ? "kapali" : "toplu"))}
              >
                {acik === "toplu" ? "Kapat" : "Toplu ekle"}
              </Buton>
              <Buton onClick={() => setAcik((a) => (a === "tek" ? "kapali" : "tek"))}>
                {acik === "tek" ? "Kapat" : "Kitap ekle"}
              </Buton>
            </div>
          )}
        </div>
      </FiltreKarti>

      {acik === "tek" && <YeniKitap dersler={dersler} onKapat={() => setAcik("kapali")} />}
      {acik === "toplu" && <TopluKitap dersler={dersler} onKapat={() => setAcik("kapali")} />}

      {havuz.isPending ? (
        <Iskelet satir={5} />
      ) : havuz.isError ? (
        <BosDurum
          ton="hata"
          baslik="Kaynak havuzu alınamadı"
          aciklama={havuz.error instanceof ApiHatasi ? havuz.error.message : undefined}
          eylem={
            <Buton gorunum="ikincil" onClick={() => void havuz.refetch()}>
              Tekrar dene
            </Buton>
          }
        />
      ) : havuz.data.length === 0 ? (
        /*
          BOŞ ≠ BOZUK, ve boşun İKİ TÜRÜ var (teslim çizimi
          `BosVeBozuk.dc.html`). Süzgeç daralttıysa çözüm "süzgeci
          temizle", havuz gerçekten boşsa çözüm "kitap ekle". İkisini
          aynı metinle geçmek, kullanıcıya yanlış işi yaptırır.
        */
        dersId || ara.trim() ? (
          <BosDurum
            ikon="bos"
            baslik="Bu süzgeçte kaynak yok"
            aciklama="Havuzda kayıt var ama süzgeç daralttı."
            eylem={
              <Buton
                gorunum="ikincil"
                onClick={() => {
                  setDersId("");
                  setAra("");
                }}
              >
                Süzgeci temizle
              </Buton>
            }
          />
        ) : (
          /*
            AÇIKLAMA YETKİYE BAĞLI — `OgrenciKaynaklari` ile aynı hata
            buradaydı: metin "olduğu gibi YAPIŞTIRABİLİRSİNİZ" diyor,
            düğme ise `yonetebilir` false iken hiç çizilmiyor. Yetkisi
            olmayan kişi yapıştırma yolunu arıyor ve bulamıyor.

            Rol adı yazılmıyor: roller değişken, izinler sabit
            (CLAUDE.md). Ekrana gömülen unvan, firma rolü yeniden
            adlandırdığı gün sessizce yanlış olur.
          */
          <BosDurum
            ikon="mufredat"
            baslik="Kaynak havuzu boş"
            aciklama={
              yonetebilir
                ? "Program hazırlarken kitap seçilebilmesi için havuzun dolu olması gerekiyor. Listeyi tek tek yazmak yerine olduğu gibi yapıştırabilirsiniz."
                : "Program hazırlarken kitap seçilebilmesi için havuzun dolu olması gerekiyor. Havuzu yönetme yetkisi sizde değil — akademik kaynakları yöneten bir yetkiliye söyleyin."
            }
            eylem={
              yonetebilir ? <Buton onClick={() => setAcik("toplu")}>Toplu ekle</Buton> : undefined
            }
          />
        )
      ) : (
        <>
          <p className="text-notr-600 text-sm">{havuz.data.length} kaynak</p>
          <Kart gorunum="unk" olarak="div" className="p-2 md:p-4">
            <Tablo
              baslikTuru="degrade"
              satirlar={havuz.data}
              anahtar={(k) => k.id}
              kartBasligi={0}
              sutunlar={[
                {
                  baslik: "Kitap",
                  mobil: true,
                  className: "font-medium",
                  hucre: (k) => (
                    <span className="flex flex-wrap items-center gap-1.5">
                      {k.ad}
                      {k.arsivlendiMi && <Rozet ton="notr">Arşiv</Rozet>}
                    </span>
                  ),
                },
                { baslik: "Yayınevi", mobil: true, hucre: (k) => k.yayinevi ?? "—" },
                { baslik: "Ders", mobil: true, hucre: (k) => k.ders ?? "—" },
                {
                  baslik: "Sayfa",
                  sayisal: true,
                  // ÖLÇÜLMEDİ ≠ SIFIR: toplam sayfa girilmemiş olabilir.
                  hucre: (k) => k.toplamSayfa ?? "—",
                },
                { baslik: "Soru", sayisal: true, hucre: (k) => k.toplamSoru ?? "—" },
                {
                  baslik: "Kullanan",
                  sayisal: true,
                  hucre: (k) => k.kullananOgrenci,
                },
                ...(yonetebilir
                  ? [
                      {
                        baslik: "İşlem",
                        mobil: true,
                        hucre: (k: KitapSatiri) =>
                          k.arsivlendiMi ? (
                            <span className="text-notr-600 text-sm">—</span>
                          ) : (
                            <KitapIslemleri kitap={k} dersler={dersler} />
                          ),
                      },
                    ]
                  : []),
              ]}
            />
          </Kart>
        </>
      )}
    </div>
  );
}

/**
 * Satır işlemleri — düzenle ve havuzdan çıkar.
 *
 * DÜZENLEME AYRI BİR İHTİYAÇ: toplu girişte kitap adları geliyor ama
 * sayfa/soru sayısı gelmiyor (yapıştırılan listede yok). O sayılar
 * girilmeden ilerleme yüzdesi hesaplanamıyor — düzenleme olmadan
 * toplu girilen her kitap kalıcı olarak "—" kalırdı.
 */
function KitapIslemleri({
  kitap,
  dersler,
}: {
  kitap: KitapSatiri;
  dersler: Array<{ id: string; ad: string }>;
}) {
  const [acik, setAcik] = useState<"kapali" | "duzenle" | "arsiv">("kapali");

  if (acik === "duzenle") {
    return <KitapDuzenle kitap={kitap} dersler={dersler} onKapat={() => setAcik("kapali")} />;
  }
  if (acik === "arsiv") {
    return <KitapArsivle kitap={kitap} onKapat={() => setAcik("kapali")} />;
  }
  return (
    <div className="flex flex-wrap gap-1">
      <Buton gorunum="sessiz" onClick={() => setAcik("duzenle")}>
        Düzenle
      </Buton>
      <Buton gorunum="sessiz" onClick={() => setAcik("arsiv")}>
        Havuzdan çıkar
      </Buton>
    </div>
  );
}

function KitapDuzenle({
  kitap,
  dersler,
  onKapat,
}: {
  kitap: KitapSatiri;
  dersler: Array<{ id: string; ad: string }>;
  onKapat: () => void;
}) {
  const qc = useQueryClient();
  const [veri, setVeri] = useState<Partial<KitapGirdisi>>({
    ad: kitap.ad,
    yayinevi: kitap.yayinevi,
    dersId: kitap.dersId,
    seviye: kitap.seviye,
    baskiYili: kitap.baskiYili,
    toplamSayfa: kitap.toplamSayfa,
    toplamSoru: kitap.toplamSoru,
  });

  const kaydet = useMutation({
    mutationFn: () => kitapGuncelle(kitap.id, { ...veri, ad: veri.ad?.trim() }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["kaynak-havuz"] });
      onKapat();
    },
  });

  const sayi = (v: string): number | null => (v.trim() === "" ? null : Number(v));

  return (
    <div className="rounded-kart border-notr-200 shadow-kart bg-notr-50 flex flex-col gap-3 border p-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <Alan
          etiket="Kitap adı"
          value={veri.ad ?? ""}
          onChange={(e) => setVeri((v) => ({ ...v, ad: e.target.value }))}
        />
        <Alan
          etiket="Yayınevi"
          value={veri.yayinevi ?? ""}
          onChange={(e) => setVeri((v) => ({ ...v, yayinevi: e.target.value }))}
        />
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        <Secim
          etiket="Ders"
          value={veri.dersId ?? ""}
          onChange={(e) => setVeri((v) => ({ ...v, dersId: e.target.value || null }))}
        >
          <option value="">Ders seçilmedi</option>
          {dersler.map((d) => (
            <option key={d.id} value={d.id}>
              {d.ad}
            </option>
          ))}
        </Secim>
        <Alan
          etiket="Toplam sayfa"
          type="number"
          value={veri.toplamSayfa ?? ""}
          onChange={(e) => setVeri((v) => ({ ...v, toplamSayfa: sayi(e.target.value) }))}
          yardim="Girilmezse ilerleme yüzdesi hesaplanmaz."
        />
        <Alan
          etiket="Toplam soru"
          type="number"
          value={veri.toplamSoru ?? ""}
          onChange={(e) => setVeri((v) => ({ ...v, toplamSoru: sayi(e.target.value) }))}
        />
      </div>

      {kaydet.isError && (
        <p role="alert" className="text-hata-500 text-sm font-medium">
          {kaydet.error instanceof ApiHatasi ? kaydet.error.message : "Kaydedilemedi."}
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        <Buton
          yukleniyor={kaydet.isPending}
          disabled={(veri.ad ?? "").trim().length === 0}
          onClick={() => kaydet.mutate()}
        >
          Kaydet
        </Buton>
        <Buton gorunum="ikincil" onClick={onKapat}>
          Vazgeç
        </Buton>
      </div>
    </div>
  );
}

function KitapArsivle({ kitap, onKapat }: { kitap: KitapSatiri; onKapat: () => void }) {
  const qc = useQueryClient();
  const [gerekce, setGerekce] = useState("");

  const arsivle = useMutation({
    mutationFn: () => kitapArsivle(kitap.id, gerekce.trim()),
    onSuccess: async () => {
      onKapat();
      await qc.invalidateQueries({ queryKey: ["kaynak-havuz"] });
    },
  });

  return (
    <div className="rounded-kart shadow-kart border-uyari-200 bg-uyari-50 flex flex-col gap-2 border p-3">
      <p className="text-uyari-800 text-xs">
        <strong>{kitap.ad}</strong> havuzdan çıkarılacak. <strong>Silinmiyor:</strong>{" "}
        {kitap.kullananOgrenci > 0 ? (
          <>
            şu an <strong>{kitap.kullananOgrenci} öğrencinin</strong> listesinde ve o kayıtlar
            ilerlemesiyle birlikte yerinde kalıyor.
          </>
        ) : (
          "hiçbir öğrencinin listesinde değil."
        )}{" "}
        Yalnızca yeni eklemelerde seçilemez olur.
      </p>
      <Alan
        etiket="Gerekçe"
        value={gerekce}
        onChange={(e) => setGerekce(e.target.value)}
        yardim="Denetim kaydına yazılıyor."
      />
      {arsivle.isError && (
        <p role="alert" className="text-hata-500 text-sm font-medium">
          {arsivle.error instanceof ApiHatasi ? arsivle.error.message : "Çıkarılamadı."}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <Buton
          yukleniyor={arsivle.isPending}
          disabled={gerekce.trim().length < 3}
          onClick={() => arsivle.mutate()}
        >
          Havuzdan çıkar
        </Buton>
        <Buton gorunum="ikincil" onClick={onKapat}>
          Vazgeç
        </Buton>
      </div>
    </div>
  );
}

function YeniKitap({
  dersler,
  onKapat,
}: {
  dersler: Array<{ id: string; ad: string }>;
  onKapat: () => void;
}) {
  const qc = useQueryClient();
  const [veri, setVeri] = useState<KitapGirdisi>({ ad: "" });

  const ekle = useMutation({
    mutationFn: () =>
      kitapEkle({
        ...veri,
        ad: veri.ad.trim(),
        yayinevi: veri.yayinevi?.trim() || null,
        dersId: veri.dersId || null,
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["kaynak-havuz"] });
      onKapat();
    },
  });

  const sayi = (v: string): number | null => (v.trim() === "" ? null : Number(v));

  return (
    <Kart
      baslik="Havuza kitap ekle"
      aciklama="Toplam sayfa ve soru sayısı ZORUNLU DEĞİL. Girilmezse ilerleme yüzdesi hesaplanmaz ve “—” gösterilir — “%0” yazmak öğrenciyi hiç denenmediği bir şeyde başarısız göstermek olurdu."
    >
      <div className="flex flex-col gap-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Alan
            etiket="Kitap adı"
            value={veri.ad}
            onChange={(e) => setVeri((v) => ({ ...v, ad: e.target.value }))}
          />
          <Alan
            etiket="Yayınevi"
            value={veri.yayinevi ?? ""}
            onChange={(e) => setVeri((v) => ({ ...v, yayinevi: e.target.value }))}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Secim
            etiket="Ders"
            value={veri.dersId ?? ""}
            onChange={(e) => setVeri((v) => ({ ...v, dersId: e.target.value }))}
            yardim="Boş bırakılırsa kitap derse bağlanmaz."
          >
            <option value="">Ders seçilmedi</option>
            {dersler.map((d) => (
              <option key={d.id} value={d.id}>
                {d.ad}
              </option>
            ))}
          </Secim>
          <Alan
            etiket="Seviye"
            value={veri.seviye ?? ""}
            onChange={(e) => setVeri((v) => ({ ...v, seviye: e.target.value }))}
            yardim="Örnek: Başlangıç, Orta, İleri."
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <Alan
            etiket="Baskı yılı"
            type="number"
            value={veri.baskiYili ?? ""}
            onChange={(e) => setVeri((v) => ({ ...v, baskiYili: sayi(e.target.value) }))}
          />
          <Alan
            etiket="Toplam sayfa"
            type="number"
            value={veri.toplamSayfa ?? ""}
            onChange={(e) => setVeri((v) => ({ ...v, toplamSayfa: sayi(e.target.value) }))}
          />
          <Alan
            etiket="Toplam soru"
            type="number"
            value={veri.toplamSoru ?? ""}
            onChange={(e) => setVeri((v) => ({ ...v, toplamSoru: sayi(e.target.value) }))}
          />
        </div>

        {ekle.isError && (
          <p role="alert" className="text-hata-500 text-sm font-medium">
            {ekle.error instanceof ApiHatasi ? ekle.error.message : "Kitap eklenemedi."}
          </p>
        )}

        <div className="flex flex-wrap gap-2">
          <Buton
            yukleniyor={ekle.isPending}
            disabled={veri.ad.trim().length === 0}
            onClick={() => ekle.mutate()}
          >
            Kitabı ekle
          </Buton>
          <Buton gorunum="ikincil" onClick={onKapat}>
            Vazgeç
          </Buton>
        </div>
      </div>
    </Kart>
  );
}

/**
 * TOPLU KİTAP GİRİŞİ — F-ACA-03 deseninin aynısı.
 *
 * Önizleme AYRI BİR UÇ DEĞİL, aynı ucun bayrağı: iki ayrı uç olsaydı
 * önizlemenin gösterdiği ile uygulamanın yaptığı bir gün ayrışır ve
 * kullanıcı gördüğünden başkasını kaydederdi.
 */
function TopluKitap({
  dersler,
  onKapat,
}: {
  dersler: Array<{ id: string; ad: string }>;
  onKapat: () => void;
}) {
  const qc = useQueryClient();
  const [dersId, setDersId] = useState("");
  const [metin, setMetin] = useState("");
  const [sonuc, setSonuc] = useState<TopluKaynakSonuc | null>(null);

  const gonder = useMutation({
    mutationFn: (onIzleme: boolean) => kitapTopluEkle({ dersId: dersId || null, metin, onIzleme }),
    onSuccess: async (s) => {
      setSonuc(s);
      if (!s.onIzleme) {
        setMetin("");
        await qc.invalidateQueries({ queryKey: ["kaynak-havuz"] });
      }
    },
  });

  const bos = metin.trim().length === 0;

  return (
    <Kart
      baslik="Havuza toplu kitap ekle"
      aciklama="Her satıra bir kaynak. İkinci sütun (sekme ya da noktalı virgül) yayınevidir."
    >
      <div className="flex flex-col gap-3">
        <Secim etiket="Ders" value={dersId} onChange={(e) => setDersId(e.target.value)}>
          <option value="">Ders seçilmedi</option>
          {dersler.map((d) => (
            <option key={d.id} value={d.id}>
              {d.ad}
            </option>
          ))}
        </Secim>

        <MetinAlani
          etiket="Liste"
          rows={7}
          value={metin}
          onChange={(e) => setMetin(e.target.value)}
          placeholder={"345 Soru Matematik\tPalme\nTYT Fizik Soru Bankası\tKarekök"}
        />

        {/*
          Sonuç paneli DUYURULUYOR (`role="status"`). Toplu içe aktarma
          bittiğinde ekranda beliren tek geri bildirim bu; ekran okuyucu
          kullanıcısı onu duymazsa işin bitip bitmediğini bilemez.
          Ayrım özellikle önemli: "eklenecek — henüz kaydedilmedi" ile
          "eklendi" bambaşka iki durum ve sessiz kalırsa kullanıcı
          önizlemeyi tamamlanmış sanabilir.
        */}
        {sonuc && (
          <div role="status" className="rounded-kart bg-notr-50 flex flex-col gap-2 p-3">
            <p className="text-notr-800 text-sm font-medium">
              {sonuc.onIzleme
                ? `${sonuc.eklenecek?.length ?? 0} satır eklenecek — henüz kaydedilmedi.`
                : `${sonuc.eklenen ?? 0} kaynak eklendi.`}
            </p>

            {/*
              ELENEN SATIRLAR TEK TEK gösteriliyor. "12 satır elendi"
              demek, kullanıcıya hangilerini düzelteceğini söylemiyor —
              ve düzeltemediği satır hiçbir öğrencinin listesine
              girmiyor.
            */}
            {sonuc.elenen.length > 0 && (
              <div className="flex flex-col gap-1">
                <p className="text-uyari-800 text-sm font-medium">
                  {sonuc.elenen.length} satır alınmadı:
                </p>
                <ul className="flex flex-col gap-0.5">
                  {sonuc.elenen.map((e) => (
                    <li key={e.satirNo} className="text-notr-600 text-xs">
                      <span className="font-medium">{e.satirNo}. satır</span> — {e.mesaj}
                      {e.ham.trim().length > 0 && (
                        <span className="text-notr-600"> ({e.ham.trim()})</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {gonder.isError && (
          <p role="alert" className="text-hata-500 text-sm font-medium">
            {gonder.error instanceof ApiHatasi ? gonder.error.message : "İşlem tamamlanamadı."}
          </p>
        )}

        <div className="flex flex-wrap gap-2">
          <Buton
            gorunum="ikincil"
            disabled={bos}
            yukleniyor={gonder.isPending && gonder.variables === true}
            onClick={() => gonder.mutate(true)}
          >
            Önizle
          </Buton>
          <Buton
            disabled={bos}
            yukleniyor={gonder.isPending && gonder.variables === false}
            onClick={() => gonder.mutate(false)}
          >
            Uygula
          </Buton>
          <Buton gorunum="sessiz" onClick={onKapat}>
            Kapat
          </Buton>
        </div>
      </div>
    </Kart>
  );
}
